import {
  Text,
  Box,
  AspectRatio,
  SimpleGrid,
  Button,
  Link,
  useTheme,
} from '@chakra-ui/react'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'

import { logEvent } from '@/lib/gtm'

import List from './List'

export interface Props {
  id?: string
  list: string[]
  header: React.ReactNode
  logo: React.ReactNode
  image: string | StaticImageData
  href: string
  index: number
  bgColor: string
  title?: string
}

function Product({
  id,
  image,
  header,
  list,
  href,
  logo,
  index,
  bgColor,
  title = '',
}: Props): React.ReactElement {
  const { t } = useTranslation('otherServices')
  const { colors } = useTheme()

  const isOdd = index % 2 === 0

  return (
    <Box id={id} sx={{ bgColor: isOdd ? 'white' : 'gray.50', width: 'full' }}>
      <SimpleGrid
        layerStyle="container"
        sx={{
          gridTemplateAreas: {
            base: '"logo" "image" "header" "list" "link"',
            md: isOdd
              ? '"image logo" "image header" "image list" "image link"'
              : '"logo image" "header image" "list image" "link image"',
          },
          gridTemplateColumns: {
            base: '1fr',
            md: '1fr 1fr',
          },
          gridGap: {
            base: 6,
            md: 10,
          },
          position: { md: 'relative' },
          pt: {
            base: 24,
            md: 36,
          },
          pb: {
            base: 16,
            md: 36,
          },
        }}
      >
        <Box
          sx={{
            gridArea: 'logo',
            justifySelf: { base: 'center', md: 'flex-start' },
            position: { base: 'relative', md: 'unset' },
          }}
        >
          <Text
            sx={{
              position: 'absolute',
              fontSize: { base: '200px', md: '320px' },
              color: { base: 'primary.50', md: 'transparent' },
              WebkitTextStroke: {
                md: `2px ${
                  isOdd ? colors.primary[400] : colors.secondary[800]
                }`,
              },
              left: { base: '50%', md: isOdd ? '0%' : 'unset' },
              right: { md: isOdd ? 'unset' : '0%' },
              top: {
                base: '50%',
                md: 'unset',
              },
              transform: {
                base: 'translate3d(-50%, -50%, 0)',
                md: isOdd
                  ? 'translate3d(-60%, -40%, 0)'
                  : 'translate3d(60%, -40%, 0)',
              },
              fontWeight: 'bold',
            }}
          >
            {index + 1}
          </Text>
          <Box sx={{ position: 'relative' }}>{logo}</Box>
        </Box>
        <Box sx={{ gridArea: 'image' }}>
          <AspectRatio ratio={636 / 440} sx={{ boxShadow: 'lg' }}>
            <Image
              src={image}
              alt={title}
              placeholder="blur"
              layout="fill"
              objectFit="cover"
              blurDataURL={typeof image === 'string' ? image : undefined}
            />
          </AspectRatio>
        </Box>
        <Text sx={{ gridArea: 'header', fontSize: { base: 'md', md: 'xl' } }}>
          {header}
        </Text>
        <List bgColor={bgColor} items={list} />
        <Button
          as={Link}
          isExternal
          href={href}
          size="xl"
          layerStyle="actionable"
          onClick={() => {
            logEvent({
              ga: {
                category: 'Other Services',
                action: `Click ${title} See More`,
              },
            })
          }}
          sx={{
            justifySelf: { base: 'center', md: 'flex-start' },
            gridArea: 'link',
            color: 'white !important',
            bg: `${bgColor} !important`,
            minW: {
              base: '256px',
              md: '256px',
            },
          }}
        >
          {t('cta.linkButton')}
        </Button>
      </SimpleGrid>
    </Box>
  )
}

export default Product
